/**
 * Building Step - Form Component
 *
 * Step 1 of the Energy Certificate Simulator wizard.
 * Collects basic building information: area, period, type, floors.
 */

import {
  Stack,
  NumberInput,
  SegmentedControl,
  Text,
  Slider,
  Group,
  ThemeIcon,
  Paper,
  Collapse,
} from '@mantine/core';
import {
  IconHome,
  IconBuilding,
  IconBuildingCommunity,
  IconBuildingSkyscraper,
  IconMapPin,
} from '@tabler/icons-react';
import type { UseFormReturnType } from '@mantine/form';
import type {
  SimulatorInput,
  BuildingPeriod,
  BuildingType,
  ApartmentFloorPosition,
  ApartmentOrientation,
  ClimateZone,
} from '../../types';

// =============================================================================
// CONSTANTS
// =============================================================================

const BUILDING_PERIODS: { value: BuildingPeriod; label: string; description: string }[] = [
  { value: 'before_1945', label: 'Przed 1945', description: 'Przedwojenny, cegła pełna, brak izolacji' },
  { value: '1945_1966', label: '1945-1966', description: 'Wielka płyta, minimalna izolacja termiczna' },
  { value: '1967_1985', label: '1967-1985', description: 'Ulepszona wielka płyta, słaba izolacja' },
  { value: '1986_1998', label: '1986-1998', description: 'Normy PN-82, pierwsze wymagania cieplne' },
  { value: '1999_2008', label: '1999-2008', description: 'Normy WT 1998, lepsza izolacja' },
  { value: '2009_2013', label: '2009-2013', description: 'Normy WT 2008, dobra izolacja' },
  { value: '2014_2020', label: '2014-2020', description: 'Normy WT 2013, wysoka izolacja' },
  { value: '2021_plus', label: '2021 i nowsze', description: 'Normy WT 2021, budynki energooszczędne' },
];

const BUILDING_TYPES: { value: BuildingType; label: string; icon: typeof IconHome; description: string }[] = [
  { value: 'detached', label: 'Wolnostojący', icon: IconHome, description: '4 ściany zewnętrzne, największe straty ciepła' },
  { value: 'semi_detached', label: 'Bliźniak', icon: IconBuilding, description: '3 ściany zewnętrzne, jedna wspólna' },
  { value: 'terraced', label: 'Szeregowy', icon: IconBuildingCommunity, description: '2 ściany zewnętrzne, mniejsze straty' },
  { value: 'apartment', label: 'Apartament', icon: IconBuildingSkyscraper, description: 'Minimalne straty, sąsiedzi ogrzewają' },
];

const OCCUPANT_OPTIONS: { value: string; label: string }[] = [
  { value: '1', label: '1 osoba' },
  { value: '2', label: '2 osoby' },
  { value: '3', label: '3 osoby' },
  { value: '4', label: '4 osoby' },
  { value: '5', label: '5+ osób' },
];

const FLOOR_OPTIONS: { value: string; label: string; description: string }[] = [
  { value: '1', label: '1 kondygnacja', description: 'Parterowy, duży dach i podłoga' },
  { value: '2', label: '2 kondygnacje', description: 'Standardowy dom jednorodzinny' },
  { value: '3', label: '3 kondygnacje', description: 'Większy dom, lepszy współczynnik A/V' },
  { value: '4', label: '4+ kondygnacje', description: 'Wielopiętrowy, optymalny kształt' },
];

const APARTMENT_FLOOR_POSITIONS: { value: ApartmentFloorPosition; label: string }[] = [
  { value: 'ground', label: 'Parter' },
  { value: 'middle', label: 'Środkowe piętro' },
  { value: 'top', label: 'Ostatnie piętro' },
];

const APARTMENT_ORIENTATIONS: { value: ApartmentOrientation; label: string; description: string }[] = [
  { value: 'south', label: 'Południe', description: 'Maksymalne zyski słoneczne zimą' },
  { value: 'southeast', label: 'Południowy-wschód', description: 'Dobre zyski, poranne słońce' },
  { value: 'southwest', label: 'Południowy-zachód', description: 'Dobre zyski, popołudniowe słońce' },
  { value: 'east', label: 'Wschód', description: 'Poranne słońce, umiarkowane zyski' },
  { value: 'west', label: 'Zachód', description: 'Popołudniowe słońce, umiarkowane zyski' },
  { value: 'north', label: 'Północ', description: 'Minimalne zyski słoneczne' },
  { value: 'northeast', label: 'Północny-wschód', description: 'Niewielkie zyski poranne' },
  { value: 'northwest', label: 'Północny-zachód', description: 'Niewielkie zyski popołudniowe' },
];

const CLIMATE_ZONE_OPTIONS: { value: ClimateZone; label: string; description: string }[] = [
  { value: 'I', label: 'Strefa I (najcieplejsza)', description: 'Szczecin, Wrocław, Zielona Góra' },
  { value: 'II', label: 'Strefa II', description: 'Poznań, Łódź, Kraków' },
  { value: 'III', label: 'Strefa III (domyślna)', description: 'Warszawa, Lublin, Rzeszów' },
  { value: 'IV', label: 'Strefa IV', description: 'Olsztyn, Białystok, Kielce' },
  { value: 'V', label: 'Strefa V (najzimniejsza)', description: 'Suwałki, Zakopane, regiony górskie' },
];

// =============================================================================
// COMPONENT
// =============================================================================

export interface BuildingStepProps {
  /** Mantine form instance */
  form: UseFormReturnType<SimulatorInput>;
}

/**
 * Building data collection step
 *
 * Collects:
 * - Usable floor area (20-500 m²)
 * - Building construction period
 * - Building type (detached, semi-detached, terraced, apartment)
 * - Number of floors
 */
export function BuildingStep({ form }: BuildingStepProps) {
  return (
    <Stack gap="lg">
      {/* Area input */}
      <Paper p="md" withBorder>
        <Stack gap="xs">
          <Text fw={500} size="sm">
            Powierzchnia użytkowa
          </Text>
          <Text size="xs" c="dimmed">
            Całkowita powierzchnia ogrzewanych pomieszczeń
          </Text>
          <Slider
            min={20}
            max={500}
            step={5}
            marks={[
              { value: 20, label: '20' },
              { value: 100, label: '100' },
              { value: 200, label: '200' },
              { value: 300, label: '300' },
              { value: 400, label: '400' },
              { value: 500, label: '500' },
            ]}
            label={(value) => `${value} m²`}
            thumbLabel="Powierzchnia użytkowa"
            {...form.getInputProps('usableArea')}
          />
          <NumberInput
            mt="xs"
            suffix=" m²"
            min={20}
            max={500}
            {...form.getInputProps('usableArea')}
            aria-label="Powierzchnia użytkowa"
          />
        </Stack>
      </Paper>

      {/* Building period */}
      <Paper p="md" withBorder>
        <Stack gap="xs">
          <Text fw={500} size="sm">
            Okres budowy
          </Text>
          <Text size="xs" c="dimmed">
            Rok zakończenia budowy lub okres, w którym budynek powstał
          </Text>
          <SegmentedControl
            fullWidth
            orientation="vertical"
            data={BUILDING_PERIODS.map((period) => ({
              value: period.value,
              label: (
                <Group justify="space-between" w="100%" px="xs">
                  <Stack gap={0} align="flex-start">
                    <Text size="sm" ta="left">{period.label}</Text>
                    <Text size="xs" c="dimmed" ta="left">{period.description}</Text>
                  </Stack>
                </Group>
              ),
            }))}
            {...form.getInputProps('buildingPeriod')}
            aria-label="Okres budowy budynku"
          />
        </Stack>
      </Paper>

      {/* Building type */}
      <Paper p="md" withBorder>
        <Stack gap="xs">
          <Text fw={500} size="sm">
            Typ budynku
          </Text>
          <Text size="xs" c="dimmed">
            Charakterystyka zabudowy wpływa na straty ciepła przez ściany
          </Text>
          <SegmentedControl
            fullWidth
            data={BUILDING_TYPES.map((type) => ({
              value: type.value,
              label: (
                <Group gap="xs" justify="center" wrap="nowrap">
                  <ThemeIcon variant="light" size="sm">
                    <type.icon size={14} />
                  </ThemeIcon>
                  <Text size="xs">{type.label}</Text>
                </Group>
              ),
            }))}
            {...form.getInputProps('buildingType')}
            aria-label="Typ budynku"
          />
        </Stack>
      </Paper>

      {/* Number of floors */}
      <Paper p="md" withBorder>
        <Stack gap="xs">
          <Text fw={500} size="sm">
            Liczba kondygnacji
          </Text>
          <Text size="xs" c="dimmed">
            Liczba pełnych kondygnacji nadziemnych
          </Text>
          <SegmentedControl
            fullWidth
            data={FLOOR_OPTIONS}
            value={String(form.values.floors)}
            onChange={(value) => form.setFieldValue('floors', Number(value))}
            aria-label="Liczba kondygnacji"
          />
        </Stack>
      </Paper>

      {/* Number of occupants */}
      <Paper p="md" withBorder>
        <Stack gap="xs">
          <Text fw={500} size="sm">
            Liczba mieszkańców
          </Text>
          <Text size="xs" c="dimmed">
            Wpływa na zużycie ciepłej wody użytkowej (CWU)
          </Text>
          <SegmentedControl
            fullWidth
            data={OCCUPANT_OPTIONS}
            value={String(form.values.occupants)}
            onChange={(value) => form.setFieldValue('occupants', Number(value))}
            aria-label="Liczba mieszkańców"
          />
        </Stack>
      </Paper>

      {/* Climate zone */}
      <Paper p="md" withBorder>
        <Stack gap="xs">
          <Group gap="xs">
            <ThemeIcon variant="light" color="teal">
              <IconMapPin size={18} />
            </ThemeIcon>
            <Text fw={500} size="sm">
              Strefa klimatyczna
            </Text>
          </Group>
          <Text size="xs" c="dimmed">
            Strefa klimatyczna wpływa na sezon grzewczy i zapotrzebowanie na ciepło
          </Text>
          <SegmentedControl
            fullWidth
            orientation="vertical"
            data={CLIMATE_ZONE_OPTIONS.map((zone) => ({
              value: zone.value,
              label: (
                <Group justify="space-between" w="100%" px="xs">
                  <Stack gap={0} align="flex-start">
                    <Text size="sm" ta="left">{zone.label}</Text>
                    <Text size="xs" c="dimmed" ta="left">{zone.description}</Text>
                  </Stack>
                </Group>
              ),
            }))}
            value={form.values.climateZone ?? 'III'}
            onChange={(value) => form.setFieldValue('climateZone', value as ClimateZone)}
            aria-label="Strefa klimatyczna"
          />
        </Stack>
      </Paper>

      {/* Apartment-specific fields (conditional) */}
      <Collapse in={form.values.buildingType === 'apartment'}>
        <Stack gap="lg">
          {/* Floor position for apartments */}
          <Paper p="md" withBorder>
            <Stack gap="xs">
              <Text fw={500} size="sm">
                Pozycja mieszkania
              </Text>
              <Text size="xs" c="dimmed">
                Położenie mieszkania w budynku wpływa na straty ciepła
              </Text>
              <SegmentedControl
                fullWidth
                data={APARTMENT_FLOOR_POSITIONS}
                value={form.values.apartmentFloorPosition || 'middle'}
                onChange={(value) =>
                  form.setFieldValue('apartmentFloorPosition', value as ApartmentFloorPosition)
                }
                aria-label="Pozycja mieszkania"
              />
            </Stack>
          </Paper>

          {/* Orientation for apartments */}
          <Paper p="md" withBorder>
            <Stack gap="xs">
              <Text fw={500} size="sm">
                Orientacja mieszkania
              </Text>
              <Text size="xs" c="dimmed">
                Kierunek geograficzny, w którym znajdują się główne okna
              </Text>
              <SegmentedControl
                fullWidth
                orientation="vertical"
                data={APARTMENT_ORIENTATIONS.map((orientation) => ({
                  value: orientation.value,
                  label: (
                    <Group justify="space-between" w="100%" px="xs">
                      <Stack gap={0} align="flex-start">
                        <Text size="sm" ta="left">{orientation.label}</Text>
                        <Text size="xs" c="dimmed" ta="left">{orientation.description}</Text>
                      </Stack>
                    </Group>
                  ),
                }))}
                value={form.values.apartmentOrientation || 'south'}
                onChange={(value) =>
                  form.setFieldValue('apartmentOrientation', value as ApartmentOrientation)
                }
                aria-label="Orientacja mieszkania"
              />
            </Stack>
          </Paper>
        </Stack>
      </Collapse>
    </Stack>
  );
}

export default BuildingStep;
